import type {
  APIWorkspaceAttributes,
  APIWorkspaceOverview,
  ArdoqId,
} from '@ardoq/api-types';
import { synthesizeOverviewFromWorkspace } from './workspaceOverviewSynthesis';

/**
 * Insert a synthesised overview entry, or merge with an existing entry
 * (preserving its count fields, since a workspace UPDATE event carries no
 * component/reference counts).
 */
export const upsertOne = (
  state: APIWorkspaceOverview[],
  workspace: APIWorkspaceAttributes
): APIWorkspaceOverview[] => {
  const previous = state.find(o => o._id === workspace._id);
  const overview = synthesizeOverviewFromWorkspace(workspace, previous);
  return previous
    ? state.map(o => (o._id === overview._id ? overview : o))
    : [...state, overview];
};

export const upsertMany = (
  state: APIWorkspaceOverview[],
  workspaces: APIWorkspaceAttributes[]
): APIWorkspaceOverview[] => workspaces.reduce(upsertOne, state);

/**
 * Replace the entire collection with overviews synthesised from `workspaces`,
 * preserving counts from any matching existing entries.
 */
export const replaceFromWorkspaces = (
  state: APIWorkspaceOverview[],
  workspaces: APIWorkspaceAttributes[]
): APIWorkspaceOverview[] => {
  const previousById = new Map(state.map(o => [o._id, o]));
  return workspaces.map(ws =>
    synthesizeOverviewFromWorkspace(ws, previousById.get(ws._id))
  );
};

export const removeMany = (
  state: APIWorkspaceOverview[],
  ids: ArdoqId[]
): APIWorkspaceOverview[] => {
  const idsToRemove = new Set(ids);
  return state.filter(o => !idsToRemove.has(o._id));
};
